import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import useSmoothScroll from "@/hooks/useSmoothScroll";
import Layout from "@/components/layout/Layout";
import Home from "@/pages/Home";
import Pricing from "@/pages/Pricing";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Refer from "@/pages/Refer";
import Privacy from "@/pages/Privacy";
import Login from "@/pages/Login";
import Booking from "@/pages/Booking";
import Gallery from "@/pages/Gallery";
import Dashboard from "@/pages/Dashboard";
import PaymentResult from "@/pages/PaymentResult";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";

function FullLoader() {
  return <div className="flex min-h-screen items-center justify-center bg-white"><Loader2 className="h-8 w-8 animate-spin text-brand-red" /></div>;
}

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/refer" element={<Refer />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/book" element={<Booking />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
        <Route path="/payment/cancel" element={<Protected><PaymentResult cancel /></Protected>} />
      </Route>
      <Route path="/login" element={<Login initialMode="login" />} />
      <Route path="/signup" element={<Login initialMode="signup" />} />
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useSmoothScroll();
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
